import classNames from 'classnames'
import React, { type JSX } from 'react'

import { Body2 } from '@ui/Typography'
import { AndroidIcon } from '@ui/icons/Android.icon'
import { AppleIcon } from '@ui/icons/Apple.icon'
import { FilePDFIcon } from '@ui/icons/FilePDF.icon'
import { GlobeIcon } from '@ui/icons/Globe.icon'
import { UserSquareIcon } from '@ui/icons/UserSquare.icon'
import { WindowsIcon } from '@ui/icons/Windows.icon'

import { OperatingSystemEnum } from '@model/PlatformTypeEnum'

import styles from './PlatformIcon.module.scss'

interface PlatformIconFactoryProps {
  os: number
  disabled?: boolean
}

export const PlatformIconFactory: React.FC<PlatformIconFactoryProps> = ({
  os,
  disabled = false,
}) => {
  switch (os) {
    case OperatingSystemEnum.WINDOWS:
      return <MTDownloadIcon title='Windows' icon={<WindowsIcon size={36} />} disabled={disabled} />
    case OperatingSystemEnum.OSX:
      return <MTDownloadIcon title='macOS' icon={<AppleIcon size={36} />} disabled={disabled} />
    case OperatingSystemEnum.WEB_TRADER:
      return (
        <MTDownloadIcon title='Web Trader' icon={<GlobeIcon size={36} />} disabled={disabled} />
      )
    case OperatingSystemEnum.ANDROID_APK:
      return (
        <MTDownloadIcon title='Android.APK' icon={<AndroidIcon size={36} />} disabled={disabled} />
      )
    case OperatingSystemEnum.ANDROID:
      return <MTDownloadIcon title='Android' icon={<AndroidIcon size={36} />} disabled={disabled} />

    default:
      return <MTDownloadIcon title='iOS' icon={<AppleIcon size={36} />} disabled={disabled} />
  }
}

interface ManualIconFactoryProps {
  icon: string
}

export const ManualIconFactory = (props: ManualIconFactoryProps): JSX.Element => {
  const { icon } = props

  if (icon.includes('fa-globe')) {
    return <FilePDFIcon size={40} />
  }

  if (icon.includes('fa-book-user')) {
    return <UserSquareIcon size={40} />
  }

  return <FilePDFIcon size={40} />
}

interface PlatformDownloadButtonProps {
  disabled?: boolean
  to?: string
  onClick?: () => void
  children?: React.ReactNode
  className?: string
}

export const PlatformDownloadButton: React.FC<PlatformDownloadButtonProps> = (props) => {
  const { to, disabled, children, onClick, className } = props

  const handleClick = () => {
    if (onClick) {
      return onClick()
    }

    if (to) {
      window.open(to, '_blank', 'noreferrer')
    }
  }

  return (
    <button
      onClick={handleClick}
      disabled={disabled}
      className={classNames(styles.platformButton, className, {
        [styles.disabled]: disabled,
      })}
    >
      {React.isValidElement(children)
        ? React.cloneElement(children as React.ReactElement<any>, {
            disabled,
          })
        : children}
    </button>
  )
}

interface MTDownloadIconProps {
  title: string
  icon: React.ReactNode
  disabled?: boolean
}

export const MTDownloadIcon: React.FC<MTDownloadIconProps> = (props) => {
  const { icon, title, disabled } = props

  return (
    <div
      className={classNames(styles.platformIcon, {
        [styles.disabled]: disabled,
      })}
    >
      <div>
        <div className={styles.icon}>{icon}</div>
        <Body2 className={styles.title} medium>
          {title}
        </Body2>
      </div>
    </div>
  )
}
